import { createContext, useContext, useEffect, useMemo, useState, type FC, type ReactNode } from 'react';
import { serviceRegistry, type ServiceConfig } from './ServiceRegistry';
import {
  adminServices,
  authenticationServices,
  collateralServices,
  dashboardServices,
  dealcardServices,
  fundingServices,
  titanServices,
} from './services';

/**
 * Dynamic service context that can handle any number of services
 */
interface ServiceContextType {
  services: Record<string, any>;
  getService: <T = any>(name: string) => T | undefined;
  registerService: (config: ServiceConfig) => void;
  registerServices: (configs: ServiceConfig[]) => void;
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

// Service configurations are now imported from separate modules

interface ServiceProviderProps {
  children: ReactNode;
  additionalServices?: ServiceConfig[];
  includeDefaults?: {
    dealcard?: boolean;
    funding?: boolean;
    collateral?: boolean;
    titan?: boolean;
    admin?: boolean;
    dashboard?: boolean;
  };
}

const EMPTY_ADDITIONAL_SERVICES: ServiceConfig[] = [];

/**
 * ServiceProvider that manages dynamic service registration
 */
export const ServiceProvider: FC<ServiceProviderProps> = ({
  children,
  additionalServices = EMPTY_ADDITIONAL_SERVICES,
  includeDefaults: includeDefaultsProp,
}) => {
  const includeDefaults = useMemo(
    () => ({
      dealcard: true,
      funding: true,
      collateral: true,
      titan: true,
      admin: true,
      dashboard: true,
      ...includeDefaultsProp,
    }),
    [includeDefaultsProp],
  );
  const [servicesReady, setServicesReady] = useState(false);

  // Register default services once (or when include/additional configs actually change).
  // Do not depend on a fresh object/array literal each render — that cleared the registry every time.
  useEffect(() => {
    // Clear existing services to avoid duplicates
    serviceRegistry.clear();

    // Federated remotes resolve auth through `authenticationService`, so it is never optional.
    serviceRegistry.registerServices(authenticationServices);

    // Register default services based on configuration
    if (includeDefaults.dealcard) {
      serviceRegistry.registerServices(dealcardServices);
    }
    if (includeDefaults.funding) {
      serviceRegistry.registerServices(fundingServices);
    }
    if (includeDefaults.collateral) {
      serviceRegistry.registerServices(collateralServices);
    }
    if (includeDefaults.titan) {
      serviceRegistry.registerServices(titanServices);
    }
    if (includeDefaults.admin) {
      serviceRegistry.registerServices(adminServices);
    }
    if (includeDefaults.dashboard) {
      serviceRegistry.registerServices(dashboardServices);
    }
    // Register any additional services
    if (additionalServices.length > 0) {
      serviceRegistry.registerServices(additionalServices);
    }

    setServicesReady(true);
  }, [additionalServices, includeDefaults]);

  const contextValue = useMemo<ServiceContextType>(
    () => ({
      services: serviceRegistry.getAllServices(),
      getService: <T = any,>(name: string) => serviceRegistry.getService<T>(name),
      registerService: (config: ServiceConfig) => serviceRegistry.registerService(config),
      registerServices: (configs: ServiceConfig[]) => serviceRegistry.registerServices(configs),
    }),
    [servicesReady],
  );

  return <ServiceContext.Provider value={contextValue}>{children}</ServiceContext.Provider>;
};

/**
 * Hook to access the service context
 */
export const useServices = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
};

/**
 * Hook to get a specific service by name
 */
export const useService = <T = any,>(name: string): T | undefined => {
  const { getService } = useServices();
  return getService<T>(name);
};
